import { memo, useMemo } from 'react';
import * as THREE from 'three';
import { Line, Text } from '@react-three/drei';
import { useBIMStore } from '../state/useBIMStore';
import { dispatchEntityClick, useEntityRegistryRef } from './toolEventBus';

/**
 * ImportedCadLayer — cross-visibility of linked/imported CAD (DWG/DXF)
 * files INSIDE the 2D drafting workspace (docs/plans/2d plan B4/6.3):
 * the parse result lives as a BIM 'Imported CAD' element; this layer draws
 * its line/text payload in the shared Z-up frame so XREF commands answer in
 * the 2D shell, not only in the BIM viewport.
 *
 * Each import is a LIVE reference (B4 edit bridge): the group claims its
 * entity-registry slot and routes clicks through dispatchEntityClick, so
 * the 'Imported CAD' element selects, erases and transforms (edit router)
 * like any other citizen from the 2D workbench too. Visibility toggles per
 * layer are honored through the element's hidden flag only, keeping the
 * payload simple.
 */

interface CadLine { points: [number[], number[]]; color?: string }
interface CadText { x: number; y: number; z: number; text?: string; height?: number; color?: string }

const REF_COLOR = '#9ecbff';

function ImportedCadGroup({ elementId, geom }: { elementId: string; geom: { origin?: number[]; cad?: unknown; lines?: CadLine[]; texts?: CadText[] } }) {
    const registryRef = useEntityRegistryRef(elementId, 'bim');
    const origin = (geom.origin ?? [0, 0, 0]) as [number, number, number];
    const lineSegments = useMemo(() => {
        const out: { pts: [number, number, number][]; color: string }[] = [];
        (geom.lines ?? []).forEach((ln) => {
            const [a, b] = ln.points ?? [];
            if (!a || !b) return;
            out.push({
                pts: [
                    [a[0], a[1], a[2] ?? 0] as [number, number, number],
                    [b[0], b[1], b[2] ?? 0] as [number, number, number],
                ],
                color: ln.color || REF_COLOR,
            });
        });
        return out;
    }, [geom]);

    return (
        <group
            ref={registryRef}
            position={origin}
            onClick={(e) => { e.stopPropagation(); dispatchEntityClick(elementId, e as never); }}
        >
            {lineSegments.map((seg, i) => (
                <Line key={i} points={seg.pts} color={seg.color} lineWidth={1} />
            ))}
            {(geom.texts ?? []).slice(0, 2000).map((t, i) => (
                <Text
                    key={`t${i}`}
                    position={[t.x, t.y, t.z + 0.02]}
                    fontSize={t.height || 0.25}
                    color={t.color || REF_COLOR}
                    anchorX="left"
                    anchorY="bottom"
                >
                    {String(t.text ?? '')}
                </Text>
            ))}
        </group>
    );
}

export const ImportedCadLayer = memo(function ImportedCadLayer() {
    const elements = useBIMStore((s) => s.project.elements);

    const imports = useMemo(() => {
        const out: { id: string; geom: { origin?: number[]; lines?: CadLine[]; texts?: CadText[] } }[] = [];
        elements.forEach((el: any) => {
            if (el?.type !== 'Imported CAD') return;
            const geom = el.geometryData;
            if (!geom || !Array.isArray(geom.lines) || geom.lines.length === 0) return;
            out.push({ id: el.id, geom });
        });
        return out;
    }, [elements]);

    if (imports.length === 0) return null;
    return (
        <group>
            {imports.map((imp) => <ImportedCadGroup key={imp.id} elementId={imp.id} geom={imp.geom} />)}
        </group>
    );
});
